import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCompetencyCourse } from '../hooks/useCompetency';

const CompetencyCourseDetail = () => {
  const { courseId } = useParams();
  const { course, loading, error } = useCompetencyCourse(parseInt(courseId));
  const [enrolling, setEnrolling] = useState(false);
  const [enrollSuccess, setEnrollSuccess] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = localStorage.getItem('userRole');

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      // For now, just show success - actual enrollment would call the API
      // await competencyService.enrollLearner({ course_id: parseInt(courseId), learner_id: user.id });
      setEnrollSuccess(true);
      setTimeout(() => {
        window.location.href = '/competency/my-courses';
      }, 2000);
    } catch (err) {
      alert('Failed to enroll: ' + err.message);
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <p>Loading course details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
          <strong>Error:</strong> {error}
        </div>
        <Link to="/competency/courses" style={{ display: 'inline-block', marginTop: '20px' }}>
          ← Back to Courses
        </Link>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container">
        <p>Course not found</p>
        <Link to="/competency/courses">← Back to Courses</Link>
      </div>
    );
  }

  return (
    <div className="container">
      <Link to="/competency/courses" style={{ display: 'inline-block', marginBottom: '20px', color: '#D4AF37' }}>
        ← Back to All Courses
      </Link>

      {enrollSuccess && (
        <div style={{
          padding: '15px',
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          marginBottom: '20px',
          color: '#155724'
        }}>
          <strong>Success!</strong> You have enrolled in this course. Redirecting to My Courses...
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
        {/* Main Content */}
        <div>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <h1 style={{ margin: 0 }}>{course.title}</h1>
              <span style={{
                background: '#D4AF37',
                color: '#fff',
                padding: '5px 12px',
                borderRadius: '12px',
                fontSize: '14px'
              }}>
                {course.framework?.cpd_hours || 20} CPD Hours
              </span>
            </div>
            <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
              <strong>Course Code:</strong> {course.course_code}
            </p>
          </div>

          <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#444' }}>
            {course.description || 'Professional competency qualification'}
          </p>

          {/* Learning Objectives */}
          {course.learning_objectives && course.learning_objectives.length > 0 && (
            <div style={{ marginTop: '30px' }}>
              <h2 style={{ borderBottom: '2px solid #D4AF37', paddingBottom: '10px' }}>
                Learning Objectives
              </h2>
              <ul style={{ lineHeight: '2' }}>
                {course.learning_objectives.map((obj, idx) => (
                  <li key={idx}>{obj}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Course Modules */}
          {course.modules && course.modules.length > 0 && (
            <div style={{ marginTop: '30px' }}>
              <h2 style={{ borderBottom: '2px solid #D4AF37', paddingBottom: '10px' }}>
                Course Modules ({course.modules.length})
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
                {course.modules.map((module, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '20px',
                      background: '#f9f9f9'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '15px' }}>
                      <div style={{
                        background: '#D4AF37',
                        color: '#fff',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}>
                        {idx + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>
                          {module.title || `Module ${idx + 1}`}
                        </h3>
                        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                          {module.description || 'Module content'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prerequisites */}
          {course.prerequisites && course.prerequisites.length > 0 && (
            <div style={{ marginTop: '30px' }}>
              <h2 style={{ borderBottom: '2px solid #D4AF37', paddingBottom: '10px' }}>
                Prerequisites
              </h2>
              <ul style={{ lineHeight: '2' }}>
                {course.prerequisites.map((prereq, idx) => (
                  <li key={idx}>{prereq}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Assessment & Exam Info */}
          <div style={{ marginTop: '30px' }}>
            <h2 style={{ borderBottom: '2px solid #D4AF37', paddingBottom: '10px' }}>
              Assessment & Examination
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
              <div style={{ padding: '20px', background: '#f0f8ff', borderRadius: '8px', border: '1px solid #2196f3' }}>
                <h4 style={{ marginTop: 0, color: '#2196f3' }}>Practical Assessment</h4>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
                  Portfolio-based assessment covering knowledge, skills, and behaviours.
                  <strong> Pass mark: 70%</strong>
                </p>
              </div>
              <div style={{ padding: '20px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ff9800' }}>
                <h4 style={{ marginTop: 0, color: '#ff9800' }}>Final Examination</h4>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
                  {course.exam_questions?.length || '30-60'} scenario-based questions.
                  Duration: {course.exam_duration_minutes || 90} minutes.
                  <strong> Pass mark: {course.exam_pass_mark || 70}%</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div style={{
            position: 'sticky',
            top: '20px',
            border: '2px solid #D4AF37',
            borderRadius: '8px',
            padding: '25px',
            background: '#fff'
          }}>
            <h3 style={{ marginTop: 0, fontSize: '20px' }}>Enroll Now</h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#D4AF37', marginBottom: '5px' }}>
                £{course.certificate_fee}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Certificate fee (pay on completion)
              </div>
            </div>

            <div style={{
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '4px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>--</strong> {course.modules?.length || 0} Learning Modules
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>--</strong> Practical Assessment
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>--</strong> Final Examination
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>--</strong> {course.framework?.cpd_hours || 20} CPD Hours
              </div>
              <div>
                <strong>--</strong> 3-year Certificate Validity
              </div>
            </div>

            {userRole === 'client' ? (
              <>
                <button
                  onClick={handleEnroll}
                  disabled={enrolling || enrollSuccess}
                  style={{
                    width: '100%',
                    padding: '15px',
                    background: enrollSuccess ? '#28a745' : '#D4AF37',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: enrolling ? 'not-allowed' : 'pointer',
                    opacity: enrolling ? 0.6 : 1,
                    marginBottom: '10px'
                  }}
                >
                  {enrolling ? 'Enrolling...' : enrollSuccess ? 'Enrolled!' : 'Enroll in Course'}
                </button>
                <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', margin: 0 }}>
                  No enrollment fee • Free to start
                </p>
              </>
            ) : userRole === 'third_party_admin' ? (
              <div style={{ padding: '15px', background: '#e3f2fd', borderRadius: '4px', fontSize: '14px' }}>
                As a Certification Body admin, you can enroll learners from your company into this course from the dashboard.
              </div>
            ) : (
              <div style={{ padding: '15px', background: '#fff3e0', borderRadius: '4px', fontSize: '14px' }}>
                Please log in as a learner to enroll in this course.
              </div>
            )}

            <div style={{
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid #eee',
              fontSize: '13px',
              color: '#666'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Status:</strong> {course.status === 'published' ? 'Published' : course.status}
              </div>
              <div>
                <strong>Certificate Valid:</strong> {course.framework?.certificate_valid_months || 36} months
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetencyCourseDetail;
